import { useState, useEffect } from "react";
import { useAuth } from "../context/AuthContext";
import { doc, getDoc } from "firebase/firestore";
import { db } from "../firebase";
import { 
  TrendingUp, 
  Calendar, 
  Target, 
  Award, 
  Clock, 
  BookOpen,
  Brain,
  Flame
} from "lucide-react";

function ProgressChart() {
  const { currentUser } = useAuth();
  const [userData, setUserData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [timeRange, setTimeRange] = useState('week'); // week, month, year

  useEffect(() => {
    const fetchUserData = async () => {
      if (!currentUser) return;
      
      try {
        const userRef = doc(db, "users", currentUser.uid);
        const snap = await getDoc(userRef);
        if (snap.exists()) {
          setUserData(snap.data());
        }
      } catch (error) {
        console.error("Error fetching user data:", error);
      } finally {
        setLoading(false);
      }
    };

    fetchUserData();
  }, [currentUser]);

  if (loading) {
    return (
      <div className="bg-gradient-to-br from-red-50 to-orange-50 rounded-3xl shadow-2xl p-8 border-4 border-red-200">
        <div className="animate-pulse">
          <div className="h-8 bg-gray-300 rounded mb-4"></div>
          <div className="h-64 bg-gray-300 rounded"></div>
        </div>
      </div>
    );
  }

  if (!userData) {
    return (
      <div className="bg-gradient-to-br from-red-50 to-orange-50 rounded-3xl shadow-2xl p-8 border-4 border-red-200">
        <p className="text-center text-gray-600">No progress data available</p>
      </div>
    );
  }

  // Calculate progress metrics
  const completedLessons = userData.completedLessons?.length || 0;
  const totalLessons = 26; // Total lessons available
  const lessonProgress = Math.round((completedLessons / totalLessons) * 100);
  
  const vocabWords = Object.keys(userData.vocabularyProgress || {}).length;
  const totalVocabWords = 300; // Total vocabulary words
  const vocabProgress = Math.round((vocabWords / totalVocabWords) * 100);
  
  const studyStreak = userData.studyStreak || 0;
  const totalStudyTime = userData.totalStudyTime || 0;
  const quizScores = userData.quizScores || [];
  const averageScore = quizScores.length > 0 
    ? Math.round(quizScores.reduce((sum, score) => sum + score.score, 0) / quizScores.length)
    : 0;

  // Generate mock data for charts (in real app, this would come from historical data)
  const generateChartData = () => {
    const days = timeRange === 'week' ? 7 : timeRange === 'month' ? 30 : 365;
    return Array.from({ length: days }, (_, i) => ({
      day: i + 1,
      lessons: Math.floor(Math.random() * 3),
      vocabulary: Math.floor(Math.random() * 10),
      time: Math.floor(Math.random() * 60) + 10
    }));
  };

  const chartData = generateChartData();

  const stats = [
    {
      title: "Lessons Completed",
      value: completedLessons,
      total: totalLessons,
      percentage: lessonProgress,
      icon: BookOpen,
      color: "from-red-500 to-red-600",
      bgColor: "from-red-100 to-red-200"
    },
    {
      title: "Vocabulary Words",
      value: vocabWords,
      total: totalVocabWords,
      percentage: vocabProgress,
      icon: Brain,
      color: "from-orange-500 to-orange-600",
      bgColor: "from-orange-100 to-orange-200"
    },
    {
      title: "Study Streak",
      value: studyStreak,
      total: 30,
      percentage: Math.min((studyStreak / 30) * 100, 100),
      icon: Flame,
      color: "from-amber-500 to-amber-600",
      bgColor: "from-amber-100 to-amber-200"
    },
    {
      title: "Average Score",
      value: averageScore,
      total: 100,
      percentage: averageScore,
      icon: Award,
      color: "from-green-500 to-green-600",
      bgColor: "from-green-100 to-green-200"
    }
  ];

  return (
    <div className="space-y-8">
      {/* Time Range Selector */}
      <div className="flex justify-center space-x-4">
        {['week', 'month', 'year'].map((range) => (
          <button
            key={range}
            onClick={() => setTimeRange(range)}
            className={`px-6 py-3 rounded-2xl font-bold transition-all duration-300 ${
              timeRange === range
                ? "bg-gradient-to-r from-red-600 to-orange-600 text-white shadow-lg"
                : "bg-white text-gray-700 hover:bg-red-50 border-2 border-red-200"
            }`}
          >
            {range.charAt(0).toUpperCase() + range.slice(1)}
          </button>
        ))}
      </div>

      {/* Progress Stats Grid */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
        {stats.map((stat, index) => (
          <div key={index} className="bg-gradient-to-br from-white to-gray-50 rounded-2xl shadow-xl p-6 border-2 border-gray-200">
            <div className="flex items-center justify-between mb-4">
              <div className={`p-3 rounded-xl bg-gradient-to-r ${stat.bgColor}`}>
                <stat.icon className="w-6 h-6 text-gray-700" />
              </div>
              <div className="text-right">
                <div className="text-2xl font-black text-gray-900">{stat.value}</div>
                <div className="text-sm text-gray-500">/ {stat.total}</div>
              </div>
            </div>
            
            <div className="mb-2">
              <div className="flex justify-between text-sm font-semibold text-gray-700">
                <span>{stat.title}</span>
                <span>{stat.percentage}%</span>
              </div>
              <div className="w-full bg-gray-200 rounded-full h-3 mt-2">
                <div 
                  className={`h-3 rounded-full bg-gradient-to-r ${stat.color} transition-all duration-500`}
                  style={{ width: `${stat.percentage}%` }}
                ></div>
              </div>
            </div>
          </div>
        ))}
      </div>

      {/* Study Time Chart */}
      <div className="bg-gradient-to-br from-red-50 to-orange-50 rounded-3xl shadow-2xl p-8 border-4 border-red-200">
        <div className="flex items-center justify-between mb-6">
          <h3 className="text-2xl font-black text-gray-900 flex items-center">
            <Clock className="w-6 h-6 mr-3 text-red-600" />
            Study Time Progress
          </h3>
          <div className="text-right">
            <div className="text-3xl font-black text-red-600">{totalStudyTime}</div>
            <div className="text-sm text-gray-600">Total Minutes</div>
          </div>
        </div>

        {/* Simple Bar Chart */}
        <div className="h-64 flex items-end justify-between space-x-1">
          {chartData.slice(-14).map((data, index) => (
            <div key={index} className="flex flex-col items-center flex-1">
              <div className="w-full bg-gradient-to-t from-red-500 to-orange-500 rounded-t-lg mb-2 transition-all duration-500 hover:from-red-600 hover:to-orange-600"
                   style={{ height: `${(data.time / 60) * 100}%` }}>
              </div>
              <div className="text-xs text-gray-600 font-semibold">
                {data.time}m
              </div>
            </div>
          ))}
        </div>
        
        <div className="mt-4 text-center text-sm text-gray-600">
          Last 14 days study time
        </div>
      </div>

      {/* Achievement Progress */}
      <div className="bg-gradient-to-br from-amber-50 to-orange-50 rounded-3xl shadow-2xl p-8 border-4 border-amber-200">
        <h3 className="text-2xl font-black text-gray-900 mb-6 flex items-center">
          <Target className="w-6 h-6 mr-3 text-amber-600" />
          Achievement Progress
        </h3>
        
        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
          <div className="space-y-4">
            <div className="flex items-center justify-between p-4 bg-white rounded-xl shadow-lg">
              <div className="flex items-center">
                <div className="w-12 h-12 bg-gradient-to-r from-red-500 to-red-600 rounded-full flex items-center justify-center mr-4">
                  <span className="text-white font-bold">1</span>
                </div>
                <div>
                  <div className="font-bold text-gray-900">First Lesson</div>
                  <div className="text-sm text-gray-600">Complete your first lesson</div>
                </div>
              </div>
              <div className={`w-8 h-8 rounded-full flex items-center justify-center ${
                completedLessons > 0 ? 'bg-green-500' : 'bg-gray-300'
              }`}>
                <span className="text-white text-sm">✓</span>
              </div>
            </div>

            <div className="flex items-center justify-between p-4 bg-white rounded-xl shadow-lg">
              <div className="flex items-center">
                <div className="w-12 h-12 bg-gradient-to-r from-orange-500 to-orange-600 rounded-full flex items-center justify-center mr-4">
                  <span className="text-white font-bold">7</span>
                </div>
                <div>
                  <div className="font-bold text-gray-900">Week Warrior</div>
                  <div className="text-sm text-gray-600">Study for 7 days straight</div>
                </div>
              </div>
              <div className={`w-8 h-8 rounded-full flex items-center justify-center ${
                studyStreak >= 7 ? 'bg-green-500' : 'bg-gray-300'
              }`}>
                <span className="text-white text-sm">✓</span>
              </div>
            </div>

            <div className="flex items-center justify-between p-4 bg-white rounded-xl shadow-lg">
              <div className="flex items-center">
                <div className="w-12 h-12 bg-gradient-to-r from-green-500 to-green-600 rounded-full flex items-center justify-center mr-4">
                  <span className="text-white font-bold">100</span>
                </div>
                <div>
                  <div className="font-bold text-gray-900">Vocabulary Master</div>
                  <div className="text-sm text-gray-600">Learn 100 vocabulary words</div>
                </div>
              </div>
              <div className={`w-8 h-8 rounded-full flex items-center justify-center ${
                vocabWords >= 100 ? 'bg-green-500' : 'bg-gray-300'
              }`}>
                <span className="text-white text-sm">✓</span>
              </div>
            </div>
          </div>

          <div className="space-y-4">
            <div className="flex items-center justify-between p-4 bg-white rounded-xl shadow-lg">
              <div className="flex items-center">
                <div className="w-12 h-12 bg-gradient-to-r from-purple-500 to-purple-600 rounded-full flex items-center justify-center mr-4">
                  <span className="text-white font-bold">30</span>
                </div>
                <div>
                  <div className="font-bold text-gray-900">Month Master</div>
                  <div className="text-sm text-gray-600">Study for 30 days straight</div>
                </div>
              </div>
              <div className={`w-8 h-8 rounded-full flex items-center justify-center ${
                studyStreak >= 30 ? 'bg-green-500' : 'bg-gray-300'
              }`}>
                <span className="text-white text-sm">✓</span>
              </div>
            </div>

            <div className="flex items-center justify-between p-4 bg-white rounded-xl shadow-lg">
              <div className="flex items-center">
                <div className="w-12 h-12 bg-gradient-to-r from-blue-500 to-blue-600 rounded-full flex items-center justify-center mr-4">
                  <span className="text-white font-bold">A+</span>
                </div>
                <div>
                  <div className="font-bold text-gray-900">Perfect Score</div>
                  <div className="text-sm text-gray-600">Get 100% on a quiz</div>
                </div>
              </div>
              <div className={`w-8 h-8 rounded-full flex items-center justify-center ${
                averageScore >= 100 ? 'bg-green-500' : 'bg-gray-300'
              }`}>
                <span className="text-white text-sm">✓</span>
              </div>
            </div>

            <div className="flex items-center justify-between p-4 bg-white rounded-xl shadow-lg">
              <div className="flex items-center">
                <div className="w-12 h-12 bg-gradient-to-r from-pink-500 to-pink-600 rounded-full flex items-center justify-center mr-4">
                  <span className="text-white font-bold">∞</span>
                </div>
                <div>
                  <div className="font-bold text-gray-900">Korean Scholar</div>
                  <div className="text-sm text-gray-600">Complete all lessons</div>
                </div>
              </div>
              <div className={`w-8 h-8 rounded-full flex items-center justify-center ${
                completedLessons >= totalLessons ? 'bg-green-500' : 'bg-gray-300'
              }`}>
                <span className="text-white text-sm">✓</span>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}

export default ProgressChart;

